import { useRef, useContext } from 'react'
import { motion } from 'framer-motion'
import { useInView } from '../../hooks/useInView'
import { RouteTransitionContext } from '../../components/AnimatedRoutes'

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
}

const fadeUp = {
  hidden: { opacity: 0, y: 80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.38, 0.005, 0.215, 1] as const,
    },
  },
}

const floatIn = (delay: number, x = 0, y = 40) => ({
  hidden: { opacity: 0, x, y },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: {
      duration: 1,
      delay,
      ease: [0.38, 0.005, 0.215, 1] as const,
    },
  },
})

/* ── Character & object assets ────────────────────── */
// const floatingGirl = require('../../assets/floating girl 1-01.svg').default
// const groundGirl = require('../../assets/ground girl-01.svg').default
// const groundBoy = require('../../assets/ground boy-01.svg').default
// const laptopGirl = require('../../assets/laptop girl-01.svg').default
// const exchangeCredit = require('../../assets/exchange credit-01.svg').default

const obj = (n: number) => require(`../../assets/objects-${String(n).padStart(2, '0')}.svg`).default

export default function Hero() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.1 })
  const isTransitioning = useContext(RouteTransitionContext)

  return (
    <section
      ref={ref}
      className="relative min-h-[95vh] bg-oak-yellow pb-8 pt-28 md:min-h-[860px] md:pb-16 md:pt-48"
    >
      {/* Text — centered in the section */}
      <div className="relative z-10 mx-auto max-w-content px-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={!isTransitioning && isInView ? 'visible' : 'hidden'}
        >
          <div className="flex flex-col items-center text-center">
            <motion.h1
              variants={fadeUp}
              className="relative top-[12vw] font-serif text-[80px] leading-[0.77] tracking-[-2.88px] text-oak-black md:top-0 md:text-[160px]"
            >
              {/* Hero copy intentionally left blank for now */}
            </motion.h1>
          </div>
        </motion.div>
      </div>

      {/* ── Floating characters & objects — positioned relative to the full section ── */}
      {/* TODO: remove z-[9999] and restore overflow-hidden on section after positioning */}
      <motion.div
        className="pointer-events-none absolute inset-0 z-[9999]"
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        {/* Top-left: floating girl */}
        {/* <motion.img
          src={floatingGirl}
          alt=""
          variants={floatIn(0.2, -40)}
          className="absolute left-[2%] top-[8%] w-[160px] md:left-[5%] md:top-[520%] md:w-[280px] lg:left-[8%] lg:w-[340px]"
        /> */}

        {/* Top-right: floating boy */}
        {/* <motion.img
          src={floatingBoy}
          alt=""
          variants={floatIn(0.35, 40)}
          className="absolute right-[2%] top-[5%] w-[150px] md:right-[5%] md:top-[3%] md:w-[260px] lg:right-[8%] lg:w-[320px]"
        /> */}

        {/* Bottom-left: ground boy */}
        {/* <motion.img
          src={groundBoy}
          alt=""
          variants={floatIn(0.5, -30, 20)}
          className="absolute top-[350%] left-[3%] w-[130px] md:top-[350%] md:left-[8%] md:w-[220px] lg:w-[260px]"
        /> */}

        {/* Bottom-right: laptop girl */}
        {/* <motion.img
          src={laptopGirl}
          alt=""
          variants={floatIn(0.4, 30, 20)}
          className="absolute top-[350%] right-[3%] w-[140px] md:top-[350%] md:right-[6%] md:w-[230px] lg:w-[270px]"
        /> */}

        {/* Bottom-center: exchange group */}
        {/* <motion.img
          src={exchangeCredit}
          alt=""
          variants={floatIn(0.6, 0, 50)}
          className="absolute top-[200%] left-[5%] w-[250px] -translate-x-1/2 md:top-[200%] md:w-[420px] lg:w-[500px]"
        /> */}

        {/* Ground girl */}
        {/* <motion.img
          src={groundGirl}
          alt=""
          variants={floatIn(0.55, -15, 30)}
          className="absolute bottom-[5%] left-[18%] w-[90px] md:bottom-[4%] md:left-[15%] md:w-[150px] lg:w-[170px]"
        /> */}

        {/* ── Scattered objects ──────────────────── */}
        <motion.img src={obj(1)} alt="" variants={floatIn(0.7)} className="absolute left-[22%] top-[12%] w-[30px] md:w-[50px]" />
        <motion.img src={obj(3)} alt="" variants={floatIn(0.75)} className="absolute right-[22%] top-[10%] w-[28px] md:w-[45px]" />
        <motion.img src={obj(5)} alt="" variants={floatIn(0.8)} className="absolute left-[6%] top-[45%] w-[25px] md:w-[40px]" />
        <motion.img src={obj(7)} alt="" variants={floatIn(0.85)} className="absolute right-[8%] top-[42%] w-[28px] md:w-[48px]" />
        <motion.img src={obj(9)} alt="" variants={floatIn(0.9)} className="absolute left-[32%] top-[20%] w-[22px] md:w-[36px]" />
        <motion.img src={obj(12)} alt="" variants={floatIn(0.8)} className="absolute bottom-[28%] right-[25%] w-[26px] md:w-[42px]" />
        <motion.img src={obj(14)} alt="" variants={floatIn(0.95)} className="absolute bottom-[32%] left-[10%] w-[24px] md:w-[40px]" />
        <motion.img src={obj(15)} alt="" variants={floatIn(0.85)} className="absolute bottom-[35%] right-[12%] w-[22px] md:w-[38px]" />
        <motion.img src={obj(18)} alt="" variants={floatIn(1.0)} className="absolute left-[40%] top-[6%] w-[26px] md:w-[42px]" />
        <motion.img src={obj(20)} alt="" variants={floatIn(0.9)} className="absolute right-[36%] top-[15%] w-[22px] md:w-[34px]" />
        <motion.img src={obj(22)} alt="" variants={floatIn(0.95)} className="absolute bottom-[22%] left-[38%] w-[24px] md:w-[40px]" />
        <motion.img src={obj(25)} alt="" variants={floatIn(1.05)} className="absolute bottom-[26%] right-[30%] w-[22px] md:w-[36px]" />
      </motion.div>
    </section>
  )
}
